import React, { useState, useContext } from 'react';
import { Button, DialogActions, Grid, TextField, CircularProgress, IconButton } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

import SalesPersonService from 'services/SalesPersonService';
import { AlertMessagesContext } from 'react-alert-messages';
import NumberField from 'components/library/NumberField';

export default function SalesPersonFormDialog({ handleClose, handleSuccess, salesPerson }) {
  const { t: tCommon } = useTranslation('common');
  const { t: tPopup } = useTranslation('popup');
  const [data, setData] = useState(salesPerson || {});
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const handleActionButton = async () => {
    setLoadingIndicator(true);
    try {
      const salespersonData = {
        ...data,
        access_code: data.password,
      };
      if (data.id) {
        await SalesPersonService.updateSalesPersons(data.id, data);
        postAlertMessage({
          text: tPopup('Salesperson updated successfully'),
          type: 'success',
        });
      } else {
        await SalesPersonService.createSalesPersons(salespersonData);
        postAlertMessage({
          text: tPopup('Salesperson created successfully'),
          type: 'success',
        });
      }
      handleSuccess();
      handleClose();
    } catch (error) {
      postAlertMessage({
        text: error.message || tPopup('An error occurred'),
        type: 'error',
      });
    } finally {
      setLoadingIndicator(false);
    }
  };

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{data.id ? tCommon('Edit Salesperson') : tCommon('New Salesperson')}</h2>
          <IconButton size="small" onClick={handleClose}><CloseIcon /></IconButton>
        </div>
        <div className={styles.inputSec}>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Name')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                label={tCommon('Name')}
                variant="outlined"
                size="small"
                fullWidth
                className={styles.numberInput}
                type="text"
                value={data.name || ''}
                onChange={(e) => {
                  setData({ ...data, name: e.target.value });
                }}
              />
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Username')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <TextField
                label={tCommon('Username')}
                variant="outlined"
                size="small"
                fullWidth
                className={styles.numberInput}
                type="text"
                value={data.username || ''}
                onChange={(e) => {
                  setData({ ...data, username: e.target.value });
                }}
              />
            </Grid>
          </Grid>
          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={4} sm={4} spacing={2} justify="flex-end" className={styles.inputLabelContainer}>
              <h3 className={styles.inputLabel}>{tCommon('Password')}</h3>
            </Grid>
            <Grid item xs={8} sm={8}>
              <NumberField
                label={tCommon('Password')}
                className={styles.numberInput}
                value={data.password || ''}
                helperText={tCommon('Password must be in numbers')}
                onChange={(e) => {
                  setData({
                    ...data,
                    password: e.target.value.substring(0, 4),
                  });
                }}
              />
            </Grid>
          </Grid>
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              className={styles.actionBtn}
              onClick={handleClose}
              disabled={loadingIndicator}
            >
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a' }}
              disabled={!data.name || data.name === '' || !data.password || data.password === '' || loadingIndicator}
              onClick={handleActionButton}
            >
              {loadingIndicator ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1.25 }} />
                  {data.id ? tCommon('Updating...') : tCommon('Creating...')}
                </>
              ) : data.id ? (
                tCommon('Update')
              ) : (
                tCommon('Create')
              )}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
